
import { lazy, Suspense, useState, useEffect, useCallback, useMemo, memo, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { frFR, arSA, enUS } from "@clerk/localizations";

import { GlobalContext } from './context/GlobalContext';
import ConsentProvider, { useConsent } from './context/ConsentContext';
import { translations } from './locales/translations_header_footer';
import Header from './components/Header';
import Footer from './components/Footer';
import Loading from './components/Loading';
import GtagUserData from './components/GtagUserData';
import { API_BASE_URL } from "./utils/api";

const GoogleTagManager = lazy(() => import('./components/GoogleTagManager'));
const ConsentBanner = lazy(() => import("./components/ConsentBanner"));

const WelcomePage = lazy(() => import('./components/WelcomePage'));
const Chat = lazy(() => import('./components/Chat'));
const FeedbackPage = lazy(() => import('./components/FeedbackPage'));
const ContactTeacher = lazy(() => import('./components/ContactTeacher'));
const Resources = lazy(() => import('./pages/Resources'));
const PDFViewer = lazy(() => import('./pages/PDFViewer'));
const PricingPage = lazy(() => import('./pages/Pricing'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));

const localizationMap = {
  en: enUS,
  fr: frFR,
  ar: arSA,
};

const LocationFallback = memo(() => {
  const location = useLocation();
  return <Loading location={location.pathname} />;
});

// "Only the free resources": a slim bar that says why the app is limited
// and how to get the full app back.
const RestrictedNotice = memo(() => {
  const { language } = useContext(GlobalContext);
  const { openPanel } = useConsent();
  const t = useMemo(
    () => translations[language || 'fr'].consent,
    [language]
  );
  return (
    <div
      className="px-4 py-2 text-center text-xs font-semibold flex flex-wrap items-center justify-center gap-2"
      style={{
        background: 'var(--bg-subtle)',
        color: 'var(--text-muted)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
      role="status"
    >
      <span>{t.restrictedOn}</span>
      <button type="button" onClick={openPanel} className="underline hover:text-orange-600">
        {t.manage}
      </button>
    </div>
  );
});

const AppContent = memo(({ language, headerButtons }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serverStatus } = useContext(GlobalContext);
  const { consent } = useConsent();

  // In the restricted mode only the free resources and the legal pages
  // are reachable, so the navigation shows the same.
  const visibleButtons = useMemo(
    () => (consent.restricted
      ? headerButtons.filter((button) => ['resources', 'faq'].includes(button.label))
      : headerButtons),
    [headerButtons, consent.restricted]
  );

  useEffect(() => {
    if (serverStatus === 'offline' && location.pathname === '/') {
      navigate('/resources', { replace: true });
    }
  }, [serverStatus, location.pathname, navigate]);

  // "Only the free resources": the student refused the optional cookies
  // and asked for the restricted app. Everything but the free resources
  // (and the legal pages, which must stay reachable) is out of reach.
  useEffect(() => {
    if (!consent.restricted) return;
    const allowed = ['/resources', '/privacy', '/terms'];
    const isResourceFile = location.pathname.startsWith('/pdf/');
    if (!allowed.includes(location.pathname) && !isResourceFile) {
      navigate('/resources', { replace: true });
    }
  }, [consent.restricted, location.pathname, navigate]);

  return (
    <div
      className="flex flex-col h-screen bg-[var(--bg-page)] text-[var(--text-main)] transition-colors"
      // The consent banner is pinned to the bottom of the viewport: it
      // reports its own height, and the app box shrinks by that much, so
      // the banner never covers the content or the composer.
      style={{ paddingBottom: 'var(--consent-banner-h, 0px)' }}
    >
      <Suspense fallback={null}>
        <GoogleTagManager />
        <ConsentBanner />
      </Suspense>
      <GtagUserData />
      <Header buttons={visibleButtons} />
      {consent.restricted && <RestrictedNotice />}
      <main className="flex-grow overflow-y-auto">
        <Suspense fallback={<LocationFallback />}>
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/chat" element={<Chat language={language} />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/contact" element={<ContactTeacher />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/pdf/*" element={<PDFViewer />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
});

const App = ({ publishableKey }) => {
  const [language, setLanguage] = useState(localStorage.getItem("lang") || navigator.language.split("-")[0] || "fr");
  const [serverStatus, setServerStatus] = useState('starting');
  const [file, setFile] = useState(null);
  const [theme, setTheme] = useState(
    () => localStorage.getItem("drihmia_theme")
      || ((window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light")
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem("drihmia_theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = language || "fr";
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);

    const wakeUpServer = async () => {
      setServerStatus('starting');
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/test/ok`, {
            cache: 'no-store',
            signal: controller.signal,
          });
          if (!alive) return;
          if (response.ok) {
            clearTimeout(timeout);
            setServerStatus('online');
            return;
          }
        } catch (_) {
          if (!alive) return;
        }
        if (attempt === 0 && alive) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
      clearTimeout(timeout);
      if (alive) setServerStatus('offline');
    };

    wakeUpServer();
    return () => {
      alive = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, []);

  const handleLanguageChange = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem("lang", lang);
  }, []);

  const headerButtons = useMemo(() => [
    { to: "/chat", label: "chat" },
    { to: "/feedback", label: "feedback" },
    { to: "/contact", label: "contact" },
    { to: "/resources", label: "resources" },
    { to: "/pricing", label: "pricing" },
    { to: "/faq", label: "faq" }
  ], []);

  const globalContextValue = useMemo(() => ({
    language,
    handleLanguageChange,
    serverStatus,
    setServerStatus,
    file,
    setFile,
    theme,
    toggleTheme
  }), [language, handleLanguageChange, serverStatus, file, theme, toggleTheme]);


  return (
    <GlobalContext.Provider value={globalContextValue}>
      <ClerkProvider publishableKey={publishableKey}
        afterSignOutUrl="/"
        signInFallbackRedirectUrl="/chat"
        signUpFallbackRedirectUrl="/chat"
        localization={localizationMap[language] || localizationMap.fr}
      >
        <Router>
          <ConsentProvider>
            <AppContent language={language} headerButtons={headerButtons} />
          </ConsentProvider>
        </Router>
      </ClerkProvider>
    </GlobalContext.Provider>
  );
};

export default App;
